/**
 * Pacientes API Service
 * CRUD operations for patient management
 */

import { api } from "@/lib/api";

const PACIENTES_URL = "/pacientes/";

// =============================================================================
// TYPES
// =============================================================================

export type TipoDocumento = "DNI" | "PASAPORTE" | "PARTIDA_NACIMIENTO" | "SIN_IDENTIFICACION";
export type Sexo = "M" | "F";

export interface Edad {
  years: number;
  months: number;
  days: number;
  texto: string;
}

export interface Paciente {
  id: number;
  tipo_documento: TipoDocumento;
  tipo_documento_display: string;
  num_documento: string;
  codigo_interno: string | null;
  primer_nombre: string;
  segundo_nombre: string;
  primer_apellido: string;
  segundo_apellido: string;
  nombre_completo: string;
  identificacion: string;
  fecha_nacimiento: string;
  sexo: Sexo;
  sexo_display: string;
  edad: Edad;
  es_menor_de_edad: boolean;
  departamento: string;
  ciudad: string;
  direccion: string;
  telefono: string;
  celular: string;
  tiene_whatsapp: boolean;
  email: string;
  foto_documento: string | null;
  foto_documento_url: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PacienteListItem {
  id: number;
  nombre_completo: string;
  identificacion: string;
  num_documento: string;
  celular: string;
  edad_texto: string;
  edad_years: number;
  sexo: Sexo;
  foto_documento_url: string | null;
  is_active: boolean;
}

export interface PacienteCreateData {
  tipo_documento: TipoDocumento;
  num_documento?: string;
  primer_nombre: string;
  segundo_nombre?: string;
  primer_apellido: string;
  segundo_apellido?: string;
  fecha_nacimiento: string;
  sexo: Sexo;
  departamento?: string;
  ciudad?: string;
  direccion?: string;
  telefono?: string;
  celular?: string;
  tiene_whatsapp?: boolean;
  email?: string;
  foto_documento?: File | null;
}

export type PacienteUpdateData = Partial<PacienteCreateData>;

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export interface PacienteFilters {
  search?: string;
  is_active?: boolean;
  sexo?: Sexo;
  page?: number;
  page_size?: number;
  ordering?: string;
}

// =============================================================================
// API FUNCTIONS
// =============================================================================

/**
 * Parameters for server-side data table (compatible with useServerDataTable)
 */
export interface FetchParams {
  page: number;
  page_size: number;
  ordering?: string;
  search?: string;
  is_active?: string;
  sexo?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Get paginated list of pacientes (compatible with useServerDataTable)
 */
export async function getPacientes(
  params: FetchParams | PacienteFilters = {}
): Promise<PaginatedResponse<PacienteListItem>> {
  const searchParams = new URLSearchParams();

  // Handle both FetchParams (from useServerDataTable) and PacienteFilters
  if (params.search) searchParams.append("search", String(params.search));
  if (params.ordering) searchParams.append("ordering", String(params.ordering));
  if (params.page) searchParams.append("page", String(params.page));
  if (params.page_size) searchParams.append("page_size", String(params.page_size));

  // Handle is_active - can be boolean (old) or string (new from useServerDataTable)
  // Skip if value is "all" (means show all, don't filter)
  if (params.is_active !== undefined && params.is_active !== "all") {
    const isActiveValue =
      typeof params.is_active === "boolean" ? params.is_active : params.is_active === "true";
    searchParams.append("is_active", String(isActiveValue));
  }

  if (params.sexo) searchParams.append("sexo", String(params.sexo));

  const url = searchParams.toString() ? `${PACIENTES_URL}?${searchParams}` : PACIENTES_URL;
  const response = await api.get<PaginatedResponse<PacienteListItem>>(url);
  return response.data;
}

/**
 * Get a single paciente by ID
 */
export async function getPaciente(id: number): Promise<Paciente> {
  const response = await api.get<Paciente>(`${PACIENTES_URL}${id}/`);
  return response.data;
}

/**
 * Create a new paciente
 */
export async function createPaciente(data: PacienteCreateData): Promise<Paciente> {
  // Use FormData if there's a file
  if (data.foto_documento instanceof File) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      }
    });
    const response = await api.post<Paciente>(PACIENTES_URL, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  // Regular JSON request
  const response = await api.post<Paciente>(PACIENTES_URL, data);
  return response.data;
}

/**
 * Update an existing paciente
 */
export async function updatePaciente(id: number, data: PacienteUpdateData): Promise<Paciente> {
  // Use FormData if there's a file
  if (data.foto_documento instanceof File) {
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (value instanceof File) {
          formData.append(key, value);
        } else {
          formData.append(key, String(value));
        }
      }
    });
    const response = await api.patch<Paciente>(`${PACIENTES_URL}${id}/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  }

  // Regular JSON request
  const response = await api.patch<Paciente>(`${PACIENTES_URL}${id}/`, data);
  return response.data;
}

/**
 * Hard delete a paciente (permanent removal)
 */
export async function deletePaciente(id: number): Promise<void> {
  await api.delete(`${PACIENTES_URL}${id}/`);
}

/**
 * Activate a paciente
 */
export async function activatePaciente(id: number): Promise<Paciente> {
  const response = await api.post<Paciente>(`${PACIENTES_URL}${id}/activate/`);
  return response.data;
}

/**
 * Deactivate a paciente (soft delete)
 */
export async function deactivatePaciente(id: number): Promise<Paciente> {
  const response = await api.post<Paciente>(`${PACIENTES_URL}${id}/deactivate/`);
  return response.data;
}

/**
 * Search pacientes by query
 */
export async function buscarPacientes(query: string, limit = 50): Promise<PacienteListItem[]> {
  const response = await api.get<PacienteListItem[]>(
    `${PACIENTES_URL}buscar/?q=${encodeURIComponent(query)}&limit=${limit}`
  );
  return response.data;
}
